/**
 * The lines about numbers — where the roast always travels with a useful
 * figure. Screen lines, ≤ 160 chars at maximal context. Weight is not exempt
 * by request of the audience; the joke still lands on the trend, not the body.
 *
 * One rule these pools learned the hard way: **a minus sign never means "over
 * budget"**. „Minus 180 kcal" was scattered through here meaning 180 *above*
 * target, and at a glance on a phone it reads as the exact opposite. An
 * overage is „viršyta", „per daug" or a plus. The genuine minus survives only
 * where it is genuinely a minus — a weight trend pointing down.
 */

import type { SassContext } from '@gramas/shared'
import { fmtKcal, fmtKg, overBy } from '../fmt.js'

type Line = (ctx: SassContext) => string

const remaining = (c: SassContext) => fmtKcal(Math.max(0, c.remainingKcal ?? 0))
const trend = (c: SassContext) => fmtKg(c.weightTrendKgWk)
const trendUp = (c: SassContext) => (c.weightTrendKgWk ?? 0) > 0

export const BUDGET: Readonly<Record<string, ReadonlyArray<Line>>> = {
  'day_over.small': [
    (c) => `Viršyta ${overBy(c)} kcal. Techniškai paklaida. Morališkai — viršyta.`,
    (c) => `${overBy(c)} kcal per daug. Smulkmena. Aš smulkmenų neužmirštu.`,
    (c) =>
      `Viršyta ${overBy(c)} kcal. Pasivaikščiojimas tai ištrintų greičiau, nei aš pakomentuočiau.`,
    (c) => `${overBy(c)} kcal viršaus. Vos vos. Bet vos vos irgi skaičiuojasi.`,
    (c) => `Viršyta ${overBy(c)} kcal. Ne tragedija. Išnaša.`,
    (c) => `Na. ${overBy(c)} kcal per daug. Suapvalinkim iki pamokos ir gyvenkim toliau.`,
    (c) => `${overBy(c)} kcal viršaus. Formaliai fiksuoju, neformaliai — numoju ranka.`,
    (c) => `Viršyta ${overBy(c)} kcal. Beveik tobula diena. „Beveik“ irgi žodis.`,
    (c) => `${overBy(c)} kcal per daug. Rytoj tas pats biudžetas lauks ramesnės rankos.`,
    (c) => `Viršyta ${overBy(c)} kcal. Istorija atleis. Aš jau beveik.`,
    (c) => `${overBy(c)} kcal virš ribos. Tiek to. Būna.`,
    (c) => `${overBy(c)} kcal viršaus. Nedramatizuokim. Bet ir nekartokim.`,
    (c) => `Viršyta ${overBy(c)} kcal. Pasižymėjau pieštuku, ne tušu.`,
    (c) => `${overBy(c)} kcal. Riba lanksti, atmintis — ne.`,
    (c) => `Aha. Viršyta ${overBy(c)} kcal. Diena verta išnašos, ne pamokslo.`,
  ],
  'day_over.big': [
    (c) => `Viršyta ${overBy(c)} kcal. Nagi. Rytoj vėl ${fmtKcal(c.kcalTarget)} kcal ir tyla.`,
    (c) => `${overBy(c)} kcal per daug. Diena išėjo į legendas. Rytojus grįžta į biudžetą.`,
    (c) => `${overBy(c)} kcal viršaus. Na po velnių. Savaitė dar gali tai amortizuoti.`,
    (c) =>
      `Biudžetas kapituliavo: ${overBy(c)} kcal. Rytoj kelsimės — ${fmtKcal(c.kcalTarget)} kcal.`,
    (c) => `Viršyta ${overBy(c)} kcal. Įspūdinga. Meniu likusiam vakarui: vanduo ir orumas.`,
    (c) => `${overBy(c)} kcal per daug. Epas. Rytoj — proza: ${fmtKcal(c.kcalTarget)} kcal.`,
    (c) => `${overBy(c)} kcal viršaus. Aš mačiau daug. Šito nepamiršiu.`,
    (c) => `Viršyta ${overBy(c)} kcal. Drama penkių veiksmų. Rytoj nauja premjera.`,
    (c) => `${overBy(c)} kcal per daug. Skaičiai neverkia. Aš irgi. Kažkas turėtų.`,
    (c) =>
      `${overBy(c)} kcal viršaus. Šiandien istorija, rytoj vėl matematika: ${fmtKcal(c.kcalTarget)} kcal.`,
    (c) => `Viršyta ${overBy(c)} kcal. Pasižymėjau storu šriftu.`,
    (c) => `Biudžetas išėjo anksčiau laiko — ${overBy(c)} kcal skirtumas. Rytoj nauja pamaina.`,
    (c) =>
      `${overBy(c)} kcal viršaus. Apie moralą negalvoju. Galvoju apie rytdienos ${fmtKcal(c.kcalTarget)}.`,
    (c) => `Viršyta ${overBy(c)} kcal. Vakaras baigtas, byla — ne. Rytoj tęsiam švariai.`,
    (c) => `${overBy(c)} kcal per daug. Faktai užrašyti. Išvados — tavo pamainai.`,
  ],
  'day_over.big.cut': [
    (c) =>
      `Deficitas šiandien žuvo — ${overBy(c)} kcal viršaus. Rytoj: ${fmtKcal(c.kcalTarget)} kcal.`,
    (c) => `Viršyta ${overBy(c)} kcal. Metimas atidėtas, branguti. Rytoj grįžtam prie darbo.`,
    (c) => `${overBy(c)} kcal viršaus per metimą. Drąsu. Savaitės vidurkis dar gali išgelbėti.`,
    (c) =>
      `Deficito diena be deficito: ${overBy(c)} kcal per daug. Rytoj vėl ${fmtKcal(c.kcalTarget)} kcal.`,
    (c) =>
      `Metimo grafikas šiandien fikcija: ${overBy(c)} kcal per daug. Fikcija baigiasi vidurnaktį.`,
    (c) => `Viršyta ${overBy(c)} kcal. Nuo vienos dienos svoris nepakils. Nuo įpročio — taip.`,
    (c) =>
      `${overBy(c)} kcal viršaus. Deficitas sako „auč“. Rytoj sakys „ačiū“, jei grįši prie ${fmtKcal(c.kcalTarget)}.`,
    (c) =>
      `Deficitas pralaimėjo mūšį — ${overBy(c)} kcal. Karas tęsiasi rytoj, ${fmtKcal(c.kcalTarget)} kcal fronte.`,
  ],
  'day_under.base': [
    (c) => `Liko ${remaining(c)} kcal. Vakaras ilgas, likutis — ne.`,
    (c) => `${remaining(c)} kcal iki ribos. Šaldytuvui tik pagarbus žvilgsnis.`,
    (c) => `Likutis: ${remaining(c)} kcal. Arbata — nulis. Šiaip priminiau.`,
    (c) => `${remaining(c)} kcal. Įtampa kaip serialo finale.`,
    (c) => `Liko ${remaining(c)} kcal. Planuok kaip juvelyras.`,
    (c) => `${remaining(c)} kcal. Dar telpa vienas protingas pasirinkimas.`,
    (c) => `Likutis ${remaining(c)} kcal. Disciplinos valanda — būtent dabar.`,
    (c) => `${remaining(c)} kcal iki lubų. Lubos arti. Vaikščiok pasilenkęs.`,
    (c) => `Liko ${remaining(c)} kcal. Geriausias desertas — miegas.`,
    (c) => `${remaining(c)} kcal. Nesuklupk ant paskutinio sausainiuko.`,
    (c) => `Likutis: ${remaining(c)} kcal. Matematika sako „gana“. Aš tik cituoju.`,
    (c) => `${remaining(c)} kcal liko. Diena beveik sutramdyta.`,
  ],
  'what_fits.base': [
    () => 'Telpa į likutį. Teoriškai.',
    () => 'Kas dar telpa. Be iliuzijų.',
    () => 'Likutis leidžia štai tiek.',
    () => 'Dar telpa. Naudok išmintingai.',
    () => 'Siūlymai iš likučio dugno.',
    () => 'Telpa. Kol kas.',
    () => 'Ką dar gali sau leisti.',
    () => 'Likučio meniu. Kuklus, bet sąžiningas.',
    () => 'Vietos dar yra. Šiek tiek.',
    () => 'Telpa į biudžetą. Patikrinau.',
    () => 'Paskutiniai laisvi centimetrai.',
    () => 'Kas telpa, kai telpa nedaug.',
    () => 'Realūs pasiūlymai realiam likučiui.',
    () => 'Dar įmanoma. Sąrašas.',
    () => 'Likutis diktuoja, aš tik užrašau.',
  ],
  'weekly_budget_over.base': [
    (c) => `Savaitė jau viršyta ${overBy(c)} kcal. Likusios dienos — paskola iš kito pirmadienio.`,
    (c) => `Savaitės biudžetas RIP: ${overBy(c)} kcal viršaus. Pirmadienis atneš naują.`,
    (c) =>
      `Savaitė išleista anksčiau laiko — ${overBy(c)} kcal skirtumas. Nauja sąskaita po savaitgalio.`,
    (c) => `Savaitės limitas krito: ${overBy(c)} kcal viršaus. Likusios dienos rašomos raudonai.`,
    (c) => `Biudžetas baigėsi anksčiau už savaitę — trūksta ${overBy(c)} kcal. Nepatogu.`,
    (c) => `Savaitė viršyta ${overBy(c)} kcal. Skaičiai nekeršija, jie tiesiog laukia pirmadienio.`,
    (c) => `Savaitės kasa tuščia, skola ${overBy(c)} kcal. Aš viską rašau.`,
    (c) => `${overBy(c)} kcal viršaus savaitės mastu. Bent jau stabilu.`,
  ],
  'weekly_budget_left.base': [
    (c) => `Liko po ${fmtKcal(c.perDayKcal)} kcal dienai. Matematika šalta, bet teisinga.`,
    (c) =>
      `Savaitėje dar yra vietos: po ${fmtKcal(c.perDayKcal)} kcal dienai. Sunkesnė vakarienė — paskola, ne nuodėmė.`,
    (c) =>
      `Po ${fmtKcal(c.perDayKcal)} kcal likusioms ${c.daysLeft ?? 0} d. Planuok kaip strategas.`,
    (c) => `Savaitės likutis: po ${fmtKcal(c.perDayKcal)} kcal dienai. Erdvės manevrui yra.`,
    (c) =>
      `${c.daysLeft ?? 0} d. po ${fmtKcal(c.perDayKcal)} kcal. Aritmetika tavo pusėje. Kol kas.`,
    (c) => `Dar po ${fmtKcal(c.perDayKcal)} kcal dienai. Savaitė atleidžia vieną sunkų vakarą.`,
    (c) => `Likutis leidžia po ${fmtKcal(c.perDayKcal)} kcal. Naudok kaip suaugęs.`,
    (c) => `Po ${fmtKcal(c.perDayKcal)} kcal per dieną iki finišo. Įveikiama.`,
  ],
  'streak.base': [
    (c) => `${c.streakDays ?? 0} d. iš eilės. Disciplina? Iconic.`,
    (c) => `${c.streakDays ?? 0} d. be pertraukos. Net aš pradedu tikėti.`,
    (c) => `${c.streakDays ?? 0} d. iš eilės. Įpročiai — patikimiausi liudininkai.`,
    (c) => `${c.streakDays ?? 0} d. iš eilės. Užsispyrimas — mano mėgstamiausia yda.`,
    (c) => `${c.streakDays ?? 0} d. serija gyva. Nepertrauk, serialas geras.`,
    (c) => `${c.streakDays ?? 0} d. iš eilės. Nuoseklumas tau tinka.`,
    (c) => `Jau ${c.streakDays ?? 0} d. Kalendorius pradeda gerbti.`,
    (c) => `${c.streakDays ?? 0} d. be spragų. Aš braukiu ašarą. Vidinę.`,
  ],
  'protein_skew.base': [
    () => 'Baltymai atvyko vienu desantu. Raumenys mieliau imtų dalimis per dieną.',
    () => 'Didžioji baltymų dalis vienu kartu. Išdalyk per valgymus — dirbs, o ne pravažiuos.',
    () => 'Baltymų grafike vienas pikas. Dramatiška. Neefektyvu. Skirstyk porcijomis.',
    () => 'Baltymai sugužėjo į vieną valgį. Kūnas nemėgsta spūsčių — paskirstyk.',
    () => 'Vienas baltymų sprogimas per dieną. Raumenims geriau serialas, ne filmas.',
    () => 'Baltymai vienu ypu. Padalyk į kelis kartus, pasisavinimas padėkos.',
  ],
  'weight_trend.base': [
    (c) =>
      trendUp(c)
        ? `Svoris juda ${trend(c)} kg/sav. aukštyn. Skaičiai nemeluoja — jie net nemoka.`
        : `Svoris juda ${trend(c)} kg/sav. Tyliai, bet užtikrintai.`,
    (c) => `Trendas: ${trend(c)} kg/sav. Svarstyklės — sąžiningiausias šių namų narys.`,
    (c) =>
      trendUp(c)
        ? `Ant svarstyklių ${trend(c)} kg/sav. Duomenys — ne kaltinimas. Kol kas.`
        : `Ant svarstyklių ${trend(c)} kg/sav. Darbas matosi.`,
    (c) => `Svorio kreivė: ${trend(c)} kg/sav. Aš tik braižau. Išvadas braižai tu.`,
    (c) => `${trend(c)} kg/sav. Tendencija aiški, sprendimai tavo.`,
    (c) => `Svoris: ${trend(c)} kg/sav. Fizika veikia. Visada veikė.`,
    (c) =>
      trendUp(c)
        ? `Kreivė aukštyn: ${trend(c)} kg/sav. Jei tai ne planas — žinau, kur ieškoti priežasties.`
        : `Kreivė žemyn: ${trend(c)} kg/sav. Nuobodu žiūrėti. Gražiai nuobodu.`,
    (c) => `Svorio kryptis: ${trend(c)} kg/sav. Faktas, ne nuomonė.`,
    (c) =>
      trendUp(c)
        ? `${trend(c)} kg/sav. į viršų. Svarstyklės tik praneša, klausimus užduodu aš.`
        : `${trend(c)} kg/sav. žemyn. Planas eina pagal scenarijų.`,
    (c) => `Savaitės svorio balansas: ${trend(c)} kg. Užrašiau be emocijų. Beveik.`,
    (c) =>
      trendUp(c)
        ? `Svoris kyla ${trend(c)} kg/sav. Arba raumenys, arba desertai. Aš žinau, kuris.`
        : `Svoris leidžiasi ${trend(c)} kg/sav. Disciplina moka. Retai, bet moka.`,
    (c) => `Kreivės nuolydis: ${trend(c)} kg/sav. Matematika nekomentuoja. Aš — saikingai.`,
    (c) =>
      trendUp(c)
        ? `Aukštyn ${trend(c)} kg/sav. Jei tai bulk — sveikinu. Jei ne — žinai, ką daryti.`
        : `Žemyn ${trend(c)} kg/sav. Tvarus tempas. Kūnas padėkos vėliau.`,
    (c) => `Svoris per savaitę: ${trend(c)} kg. Duomenys šalti, išvados — karštos.`,
    (c) =>
      trendUp(c)
        ? `Trendas ${trend(c)} kg/sav. į viršų. Svarstyklės niekada neintriguoja be reikalo.`
        : `Trendas ${trend(c)} kg/sav. žemyn. Kryptis pati už save kalba.`,
  ],
}

export const BUDGET_META: Readonly<Record<string, number>> = {
  'day_over.small': 7,
  'day_over.big': 7,
  'day_over.big.cut': 7,
  'day_under.base': 10,
  'what_fits.base': 14,
  'weekly_budget_over.base': 2,
  'weekly_budget_left.base': 4,
  'streak.base': 2,
  'protein_skew.base': 4,
  'weight_trend.base': 14,
}
